import MsgReader from '@kenjiuno/msgreader';
import PostalMime from 'postal-mime';
import { findSkuInRow, PO_VENDOR_SKU_RE } from './demPoExcelExtract';

export type PoEmailAttachment = {
  fileName: string;
  mimeType: string;
  blob: Blob;
};

export type PoEmailParseResult = {
  subject: string;
  attachments: PoEmailAttachment[];
  bodyText: string;
  bodyHtml: string;
};

const PO_ATTACHMENT_EXT = new Set(['.pdf', '.xlsx', '.xls']);
const MAX_NEST_DEPTH = 4;

function extensionOf(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx).toLowerCase() : '';
}

function stripHtml(html: string): string {
  const withoutBlocks = html.replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '');
  const withBreaks = withoutBlocks.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n');
  const text = withBreaks.replace(/<[^>]+>/g, ' ');
  return text.replace(/[ \t]+/g, ' ').replace(/\n\s*\n/g, '\n').trim();
}

function normalizePlainText(text: string): string {
  return text.replace(/[ \t]+/g, ' ').replace(/\n\s*\n/g, '\n').trim();
}

function normalizeAttachmentMime(fileName: string, mimeType: string): string {
  const ext = extensionOf(fileName);
  if (ext === '.pdf' || mimeType.toLowerCase() === 'application/pdf') return 'application/pdf';
  if (ext === '.xlsx') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (ext === '.xls') return 'application/vnd.ms-excel';
  return mimeType || 'application/octet-stream';
}

function looksLikePdf(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 4 &&
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46
  );
}

function looksLikeZip(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b;
}

function ensurePoFileName(fileName: string, content: Uint8Array): string {
  const trimmed = fileName.trim() || 'attachment';
  if (extensionOf(trimmed)) return trimmed;
  if (looksLikePdf(content)) return `${trimmed}.pdf`;
  if (looksLikeZip(content)) return `${trimmed}.xlsx`;
  return trimmed;
}

function isPoAttachment(fileName: string, mimeType: string, content?: Uint8Array): boolean {
  const ext = extensionOf(fileName);
  if (PO_ATTACHMENT_EXT.has(ext)) return true;
  const mime = mimeType.toLowerCase();
  if (
    mime === 'application/pdf' ||
    mime.includes('spreadsheet') ||
    mime.includes('excel') ||
    mime === 'application/vnd.ms-excel' ||
    mime === 'application/octet-stream'
  ) {
    if (content && (looksLikePdf(content) || looksLikeZip(content))) return true;
  }
  if (content?.length) {
    if (looksLikePdf(content)) return true;
    if (looksLikeZip(content) && (ext === '.xlsx' || ext === '.xls')) return true;
  }
  return false;
}

/** Ariba / portal HTML PO attachments — keep for neat preview + table CSV, not as PDF/Excel extract. */
function isHtmlAttachment(fileName: string, mimeType: string): boolean {
  const ext = extensionOf(fileName);
  if (ext === '.htm' || ext === '.html') return true;
  const mime = mimeType.toLowerCase();
  return mime === 'text/html' || mime.includes('text/html') || mime === 'application/xhtml+xml';
}

function ensureHtmlFileName(fileName: string): string {
  const trimmed = fileName.trim() || 'attachment';
  const ext = extensionOf(trimmed);
  if (ext === '.htm' || ext === '.html') return trimmed;
  return `${trimmed}.htm`;
}

function listHtmlAttachments(attachments: PoEmailAttachment[]): PoEmailAttachment[] {
  return attachments
    .filter((item) => isHtmlAttachment(item.fileName, item.mimeType))
    .sort((a, b) => b.blob.size - a.blob.size);
}

function toUint8Array(content: Uint8Array | ArrayBuffer | string): Uint8Array {
  if (content instanceof Uint8Array) return content;
  if (typeof content === 'string') return new TextEncoder().encode(content);
  return new Uint8Array(content);
}

function pickBestAttachment(attachments: PoEmailAttachment[]): PoEmailAttachment | undefined {
  const pdfs = attachments.filter(
    (item) => extensionOf(item.fileName) === '.pdf' || item.mimeType === 'application/pdf',
  );
  if (pdfs.length > 0) {
    return [...pdfs].sort((a, b) => b.blob.size - a.blob.size)[0];
  }

  const excel = attachments.filter((item) => ['.xlsx', '.xls'].includes(extensionOf(item.fileName)));
  if (excel.length > 0) {
    return [...excel].sort((a, b) => b.blob.size - a.blob.size)[0];
  }

  // Do not pick HTML attachments as the extract file — preview uses them separately.
  return undefined;
}

/** Convert HTML email tables (e.g. RE&S / FoodMarketHub) to CSV for local + AI extraction. */
export function htmlTablesToCsvText(html: string): string {
  const tables = extractHtmlTableRows(html);
  // FoodMarketHub-style: merge duplicate SKU tables when vendor SKUs exist.
  const merged = buildMergedLineItemsTable(tables);
  if (merged && merged.length > 0) return mergedTableToCsv(merged);

  // RE&S consolidation has Outlet + PO No + Item Name (no A####A SKUs) — keep the raw table.
  const fallback = pickLineItemsTable(tables);
  if (fallback && fallback.length > 0) return mergedTableToCsv(fallback);
  return '';
}

/** Count distinct vendor SKUs visible in email HTML (for completeness checks). */
export function countVendorSkusInHtml(html: string): number {
  const skus = new Set<string>();
  const text = stripHtmlTags(html);
  for (const match of text.matchAll(new RegExp(PO_VENDOR_SKU_RE.source, 'g'))) {
    skus.add(match[1]!.toUpperCase());
  }
  return skus.size;
}

function mergedTableToCsv(table: string[][]): string {
  return table
    .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))
    .join('\n');
}

function isLineItemsHeaderRow(row: string[]): boolean {
  const text = row.join(' ').toLowerCase();
  return text.includes('sku') && (text.includes('qty') || text.includes('quantity'));
}

function buildMergedLineItemsTable(tables: string[][][]): string[][] | null {
  const primary = pickLineItemsTable(tables);
  if (!primary || primary.length < 2) return primary;

  let headerIndex = 0;
  for (let i = 0; i < Math.min(12, primary.length); i += 1) {
    if (isLineItemsHeaderRow(primary[i] ?? [])) {
      headerIndex = i;
      break;
    }
  }

  const header = primary.slice(0, headerIndex + 1);
  const skuToRow = new Map<string, string[]>();
  const skuOrder: string[] = [];

  const considerRow = (row: string[]) => {
    const sku = findSkuInRow(row);
    if (!sku || skuToRow.has(sku)) return;
    if (isLineItemsHeaderRow(row)) return;
    skuToRow.set(sku, row);
    skuOrder.push(sku);
  };

  for (const row of primary.slice(headerIndex + 1)) {
    considerRow(row);
  }

  for (const table of tables) {
    if (table === primary) continue;
    for (const row of table) {
      considerRow(row);
    }
  }

  if (skuOrder.length === 0) return null;
  return [...header, ...skuOrder.map((sku) => skuToRow.get(sku)!)];
}

function stripHtmlTags(value: string): string {
  return value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

function tableRowsFromElement(table: HTMLTableElement): string[][] {
  const rows: string[][] = [];
  for (const row of table.querySelectorAll('tr')) {
    const cells = [...row.querySelectorAll('th, td')];
    if (cells.length === 0) continue;
    const values = cells.map((cell) => (cell.textContent ?? '').trim().replace(/\s+/g, ' '));
    if (values.some(Boolean)) rows.push(values);
  }
  return rows;
}

function parseTrCells(trHtml: string): string[] | null {
  const cellMatches = trHtml.match(/<t[hd][^>]*>([\s\S]*?)<\/t[hd]>/gi) ?? [];
  if (cellMatches.length === 0) return null;
  const values = cellMatches.map((cellHtml) => stripHtmlTags(cellHtml));
  return values.some(Boolean) ? values : null;
}

function tableRowsFromHtmlRegex(html: string): string[][][] {
  const tables: string[][][] = [];
  const tableMatches = html.match(/<table[\s\S]*?<\/table>/gi) ?? [];
  for (const tableHtml of tableMatches) {
    const rows: string[][] = [];
    const trMatches = tableHtml.match(/<tr[\s\S]*?<\/tr>/gi) ?? [];
    for (const trHtml of trMatches) {
      const values = parseTrCells(trHtml);
      if (values) rows.push(values);
    }

    const afterLastClosedTr = tableHtml.replace(/^[\s\S]*<\/tr>/i, '');
    const trailingTr = afterLastClosedTr.match(/<tr[\s\S]*?(?=<\/table>)/i)?.[0];
    if (trailingTr && !/<\/tr>/i.test(trailingTr)) {
      const values = parseTrCells(trailingTr);
      if (values) rows.push(values);
    }

    if (rows.length > 0) tables.push(rows);
  }
  return tables;
}

function extractHtmlTablesList(html: string): string[][][] {
  const seen = new Set<string>();
  const tables: string[][][] = [];

  const addTable = (rows: string[][]) => {
    if (rows.length === 0) return;
    const skuCount = rows.filter((row) => findSkuInRow(row)).length;
    const key = `${rows.length}:${skuCount}:${rows[0]?.join('|') ?? ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    tables.push(rows);
  };

  if (typeof DOMParser !== 'undefined' && html.trim()) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    for (const table of doc.querySelectorAll('table')) {
      addTable(tableRowsFromElement(table));
    }
  }

  for (const table of tableRowsFromHtmlRegex(html)) {
    addTable(table);
  }

  return tables;
}

function isConsolidationHeaderRow(row: string[]): boolean {
  const text = row.join(' ').toLowerCase();
  return (
    /\boutlet\b/.test(text) &&
    (/\bpo\s*no\b/.test(text) || /\bpo\s*number\b/.test(text) || /\bpurchase\s*order\b/.test(text))
  );
}

function scoreLineItemsTable(rows: string[][]): number {
  if (rows.length < 2) return 0;

  let bestScore = 0;
  for (let i = 0; i < Math.min(24, rows.length - 1); i += 1) {
    const headerRow = rows[i] ?? [];
    const header = headerRow.join(' ').toLowerCase();
    const isConsolidation = isConsolidationHeaderRow(headerRow);
    if (!header.includes('sku') && !isConsolidation) continue;

    let score = rows.length - i;
    if (isConsolidation) score += 80;
    if (header.includes('qty') || header.includes('quantity')) score += 10;
    if (header.includes('packaging')) score += 5;
    if (header.includes('unit price')) score += 5;
    if (header.includes('item name')) score += 5;
    const skuRows = rows.slice(i + 1).filter((row) => /\b[A-Z]\d{4}[A-Z]\b/.test(row.join(' '))).length;
    score += skuRows * 3;
    bestScore = Math.max(bestScore, score);
  }

  return bestScore;
}

function pickLineItemsTable(tables: string[][][]): string[][] | null {
  let best: string[][] | null = null;
  let bestScore = 0;

  for (const table of tables) {
    const score = scoreLineItemsTable(table);
    if (score > bestScore) {
      bestScore = score;
      best = table;
    }
  }

  return best;
}

export function extractHtmlTableRows(html: string): string[][][] {
  return extractHtmlTablesList(html);
}

async function collectEmlAttachments(
  bytes: Uint8Array,
  depth = 0,
): Promise<{ subject: string; attachments: PoEmailAttachment[]; bodyText: string; bodyHtml: string }> {
  const parser = new PostalMime();
  const email = await parser.parse(bytes);
  const subject = email.subject?.trim() || 'Email_Order';
  const attachments: PoEmailAttachment[] = [];

  for (const attachment of email.attachments ?? []) {
    const fileName = attachment.filename?.trim() || 'attachment';
    const mimeType = attachment.mimeType || '';
    const content = toUint8Array(
      attachment.content instanceof Uint8Array
        ? attachment.content
        : (attachment.content as ArrayBuffer),
    );

    const nestedMime = mimeType.toLowerCase();
    if (
      depth < MAX_NEST_DEPTH &&
      (nestedMime === 'message/rfc822' || extensionOf(fileName) === '.eml')
    ) {
      const nested = await collectEmlAttachments(content, depth + 1);
      attachments.push(...nested.attachments);
      continue;
    }

    if (isHtmlAttachment(fileName, mimeType)) {
      const resolvedName = ensureHtmlFileName(fileName);
      attachments.push({
        fileName: resolvedName,
        mimeType: 'text/html',
        blob: new Blob([content], { type: 'text/html' }),
      });
      continue;
    }

    if (!isPoAttachment(fileName, mimeType, content)) continue;

    const resolvedName = ensurePoFileName(fileName, content);
    const normalizedMime = normalizeAttachmentMime(resolvedName, mimeType);
    attachments.push({
      fileName: resolvedName,
      mimeType: normalizedMime,
      blob: new Blob([content], { type: normalizedMime }),
    });
  }

  const bodyHtml = typeof email.html === 'string' ? email.html : '';
  const bodyText = normalizePlainText(
    typeof email.text === 'string' && email.text.trim()
      ? email.text
      : bodyHtml
        ? stripHtml(bodyHtml)
        : '',
  );

  return { subject, attachments, bodyText, bodyHtml };
}

function parseMsgBytes(bytes: Uint8Array): PoEmailParseResult {
  const reader = new MsgReader(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength));
  const data = reader.getFileData();
  const subject = data.subject?.trim() || 'Email_Order';
  const attachments: PoEmailAttachment[] = [];

  for (const attachment of data.attachments ?? []) {
    const fileName = attachment.fileName?.trim() || attachment.fileNameShort?.trim() || 'attachment';
    const mimeType = 'application/octet-stream';
    const file = reader.getAttachment(attachment);
    const content = file?.content;
    if (!content) continue;
    const uint8 = content instanceof Uint8Array ? content : new Uint8Array(content as ArrayBuffer);
    if (isHtmlAttachment(fileName, mimeType)) {
      const resolvedName = ensureHtmlFileName(fileName);
      attachments.push({
        fileName: resolvedName,
        mimeType: 'text/html',
        blob: new Blob([uint8], { type: 'text/html' }),
      });
      continue;
    }

    if (!isPoAttachment(fileName, mimeType, uint8)) continue;
    const resolvedName = ensurePoFileName(fileName, uint8);
    const normalizedMime = normalizeAttachmentMime(resolvedName, mimeType);
    attachments.push({
      fileName: resolvedName,
      mimeType: normalizedMime,
      blob: new Blob([uint8], { type: normalizedMime }),
    });
  }

  const bodyHtml = typeof data.bodyHtml === 'string' ? data.bodyHtml : '';
  const bodyText = normalizePlainText(
    typeof data.body === 'string' && data.body.trim()
      ? data.body
      : bodyHtml
        ? stripHtml(bodyHtml)
        : '',
  );

  return { subject, attachments, bodyText, bodyHtml };
}

export async function parseEmailFile(file: File): Promise<PoEmailParseResult> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'msg') return parseMsgBytes(bytes);
  return collectEmlAttachments(bytes);
}

export async function parseEmlBase64(emlBase64: string, subject = 'Email_Order'): Promise<PoEmailParseResult> {
  const binary = atob(emlBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const parsed = await collectEmlAttachments(bytes);
  if (!parsed.subject || parsed.subject === 'Email_Order') {
    parsed.subject = subject;
  }
  return parsed;
}

async function bestHtmlTableCsv(parsed: PoEmailParseResult): Promise<string> {
  if (parsed.bodyHtml?.trim()) {
    const fromBody = htmlTablesToCsvText(parsed.bodyHtml);
    if (fromBody.trim()) return fromBody;
  }

  for (const htmlAtt of listHtmlAttachments(parsed.attachments)) {
    try {
      const html = await htmlAtt.blob.text();
      if (!html.trim()) continue;
      const fromAtt = htmlTablesToCsvText(html);
      if (fromAtt.trim()) return fromAtt;
    } catch {
      /* ignore unreadable HTML attachment */
    }
  }

  return '';
}

export async function resolveEmailToProcessingFile(
  parsed: PoEmailParseResult,
): Promise<{ file: File; previewText?: string }> {
  const attachment = pickBestAttachment(parsed.attachments);
  if (attachment) {
    const file = new File([attachment.blob], attachment.fileName, {
      type: attachment.mimeType || 'application/octet-stream',
    });
    return { file };
  }

  const tableCsv = await bestHtmlTableCsv(parsed);
  const body =
    tableCsv ||
    parsed.bodyText ||
    (parsed.bodyHtml ? stripHtml(parsed.bodyHtml) : '');
  if (!body.trim()) {
    // HTML attachment may still exist for preview even when tables/text are empty.
    const htmlAtt = listHtmlAttachments(parsed.attachments)[0];
    if (htmlAtt) {
      try {
        const htmlText = stripHtml(await htmlAtt.blob.text());
        if (htmlText.trim()) {
          const safeSubject = parsed.subject.replace(/[^\w\-]+/g, '_').slice(0, 50) || 'Email_Body';
          const textBlob = new Blob([`RAW EMAIL TEXT (NO TABLES):\n${htmlText}`], {
            type: 'text/plain',
          });
          return {
            file: new File([textBlob], `${safeSubject}_Body.txt`, { type: 'text/plain' }),
            previewText: htmlText,
          };
        }
      } catch {
        /* fall through */
      }
    }
    throw new Error('No valid attachment or text body found in this email.');
  }

  const safeSubject = parsed.subject.replace(/[^\w\-]+/g, '_').slice(0, 50) || 'Email_Body';
  if (tableCsv) {
    const file = new File([tableCsv], `${safeSubject}_Body.csv`, { type: 'text/csv' });
    return { file, previewText: tableCsv };
  }

  const textBlob = new Blob([`RAW EMAIL TEXT (NO TABLES):\n${body}`], { type: 'text/plain' });
  const file = new File([textBlob], `${safeSubject}_Body.txt`, { type: 'text/plain' });
  return { file, previewText: body };
}

export function emlBase64ToFile(emlBase64: string, subject = 'Email_Order'): File {
  const binary = atob(emlBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const safeSubject = subject.replace(/[^\w\-]+/g, '_').slice(0, 50) || 'Email_Order';
  return new File([bytes], `${safeSubject}.eml`, { type: 'message/rfc822' });
}
